import { PlusOutlined } from '@ant-design/icons';
import { Button, Image, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import React from 'react';
import type { DishDto } from '@/services/dishes';

interface DishSelectionTableProps {
    dishes: DishDto[];
    loading: boolean;
    selectedRowKeys: React.Key[];
    addingDishIds: Set<string>;
    excludeDishIds: string[];
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    };
    onSelectionChange: (selectedRowKeys: React.Key[]) => void;
    onTableChange: (pagination: any) => void;
    onAddSingle: (dishId: string) => void;
}

const DishSelectionTable: React.FC<DishSelectionTableProps> = ({
    dishes,
    loading,
    selectedRowKeys,
    addingDishIds,
    excludeDishIds,
    pagination,
    onSelectionChange,
    onTableChange,
    onAddSingle,
}) => {
    const rowSelection: TableRowSelection<DishDto> = {
        selectedRowKeys,
        onChange: onSelectionChange,
        getCheckboxProps: (record) => ({
            disabled: excludeDishIds.includes(record.id),
        }),
    };

    const columns: ColumnType<DishDto>[] = [
        {
            title: 'Hình ảnh',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            width: 80,
            render: (imageUrl: string | null) =>
                imageUrl ? (
                    <Image
                        src={imageUrl}
                        alt=""
                        width={50}
                        height={50}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                    />
                ) : (
                    <div
                        style={{
                            width: 50,
                            height: 50,
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            borderRadius: 4,
                            fontSize: '9px',
                        }}
                    >
                        No Image
                    </div>
                ),
        },
        {
            title: 'Tên món ăn',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: DishDto) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{text}</div>
                    {record.nameEn && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.nameEn}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Slug',
            dataIndex: 'slug',
            key: 'slug',
            width: 170,
            render: (text: string) => (
                <code style={{ fontSize: '12px' }}>{text}</code>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'Hoạt động' : 'Tạm dừng'}
                </Tag>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 100,
            render: (_: any, record: DishDto) => {
                const isExcluded = excludeDishIds.includes(record.id);
                const isAdding = addingDishIds.has(record.id);

                if (isExcluded) {
                    return (
                        <Tag color="default" style={{ margin: 0 }}>
                            Đã có
                        </Tag>
                    );
                }

                return (
                    <Tooltip title="Thêm vào bộ sưu tập">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="small"
                            loading={isAdding}
                            onClick={() => onAddSingle(record.id)}
                        >
                            Thêm
                        </Button>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={dishes}
            rowKey="id"
            loading={loading}
            rowSelection={rowSelection}
            size="small"
            pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                    `${range[0]}-${range[1]} của ${total} món ăn`,
                pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={onTableChange}
            scroll={{ y: 500 }}
        />
    );
};

export default DishSelectionTable;

